import { create } from "zustand";
import { devtools } from "zustand/middleware";

const defaultFilters = {
  supplier: "All",
  poLineStatus: "All",
  buyer: "All",
  authorizationStatus: "All",
  authorizationRequirement: "All",
  programEndUse: "All",
  // LWI-556 — Authorized to Ship is non-USA only; the FilterPanel hides the
  // select on the USA tab. Backend accepts "Authorized" / "Not Authorized" /
  // "All" as the values.
  authorizedToShip: "All",
  // LWI-591 — Task is admin/owner-gated (same gate as the edit page from
  // LWI-560); the FilterPanel hides the select for non-admin users.
  task: "All",
};

const initialState = {
  selectedRegion: "USA",
  selectedPONumber: null,
  purchaseOrderLines: [],
  searchQuery: "",
  filters: defaultFilters,
  isFilterPanelOpen: false,
  // LWI-579 / LWI-689 — admin-only review-queue presets. One of:
  //   null              — no preset applied (default)
  //   'open_no_license' — Open + Not Authorized + Authorization=Not Required
  //   'open_assigned'   — Open + Not Authorized + Authorization=Assigned
  //   'closed'          — Closed (any variant) + Not Authorized
  // Layered on top of selectedRegion. Hidden for non-admin users on the
  // frontend; backend silently ignores the param for non-admin callers
  // as defense in depth.
  reviewView: null,
};

export const usePurchaseOrdersStore = create(
  devtools(
    (set) => ({
      ...initialState,

      setSelectedRegion: (region) => set({ selectedRegion: region }),

      setSelectedPONumber: (poNumber) => set({ selectedPONumber: poNumber }),

      setPurchaseOrderLines: (lines) => set({ purchaseOrderLines: lines }),

      setSearchQuery: (query) => set({ searchQuery: query }),

      setFilters: (filters) => set({ filters }),

      resetFilters: () => set({ filters: defaultFilters }),

      setFilterPanelOpen: (open) => set({ isFilterPanelOpen: open }),

      // LWI-689 hotfix — whitelist must match the values the ToggleButton
      // actually emits. Previously included 'closed_assigned' (an older
      // draft name) but the button value is 'closed', so picking the
      // Closed tab silently fell through to null and applied no filter.
      setReviewView: (v) => set({
        reviewView: ['open_no_license', 'open_assigned', 'closed'].includes(v) ? v : null,
      }),

      addComment: (lineId, comment) =>
        set((state) => ({
          purchaseOrderLines: state.purchaseOrderLines.map((line) =>
            line.id === lineId
              ? { ...line, comments: [...(line.comments || []), comment] }
              : line
          ),
        })),

      deleteLine: (lineId) =>
        set((state) => ({
          purchaseOrderLines: state.purchaseOrderLines.filter(
            (line) => line.id !== lineId
          ),
        })),

      reset: () => set(initialState),
    }),
    { name: "PurchaseOrdersStore" }
  )
);
